import { useState } from 'react';

import { cn, ia } from 'lib/helpers/utility';
import useSavedPaymentMethods from 'lib/hooks/useSavedPaymentMethods';

import Modal from 'components/shared/Modal/Modal';
import Button from 'components/shared/Button/Button';
import Loader from 'components/shared/Loader/Loader';
import Typography from 'components/shared/Typography/Typography';
import {
  usePaymentContext,
  usePaymentContextApi
} from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';
import PayWithAchContent from 'components/Payments/NewPayment/PaymentContent/components/PaymentMethods/Payroc/PayWithAchModal/PayWithAchContent';

import InformationCard from '../PayFromKioskModal/components/InformationCard';

import PairedNewAch from './components/PairedNewAch';
import CardEntryContent from './components/CardEntryContent';
import PaymentEntryFooter from './components/PaymentEntryFooter';
import PaymentEntryHeader from './components/PaymentEntryHeader';
import { FormLoadErrorContext } from './lib/formLoadErrorContext';
import {
  FORM_LOAD_ERROR_INFO,
  PAYMENT_ENTRY_CONFIG,
  PAYMENT_ENTRY_KINDS,
  PAYMENT_MODAL_SIZE
} from './lib/constants';

const PaymentEntryModal = ({
  isOpen,
  handleClose,
  patientId,
  kind = PAYMENT_ENTRY_KINDS.CARD,
  hideSavedMethods = false
}) => {
  const config = PAYMENT_ENTRY_CONFIG[kind];
  const { terminal, values, processor } = usePaymentContext();
  const { setFieldValue } = usePaymentContextApi();
  const [formLoadError, setFormLoadError] = useState(null);

  const {
    data: savedMethods,
    active,
    setActive,
    scrollRef,
    isFetchingNextPage,
    isFetching,
    isError,
    refetch
  } = useSavedPaymentMethods({
    patientId: hideSavedMethods || !isOpen ? null : patientId,
    terminalId: terminal?.id,
    type: config.savedMethodType
  });

  const methods = hideSavedMethods ? [] : savedMethods;
  // Until the saved methods resolve there is nothing to switch to, so the modal would open
  // straight on the new-card form and jump to the list a moment later.
  const hasMethodsError = !hideSavedMethods && isOpen && isError;
  const isResolvingMethods =
    !hideSavedMethods && isOpen && !!terminal?.id && isFetching && !ia(savedMethods);
  const canGoBack = ia(methods);
  const activeKey = canGoBack ? active : config.newKey;
  const isNew = !isResolvingMethods && activeKey === config.newKey;

  const goToSavedMethods = () => setActive(config.savedKey);

  const selectCard = (cardId) => {
    setFieldValue('method', config.savedKey);
    setFieldValue('methodDetail', cardId);
    setActive(config.savedKey);
  };

  const useAnotherCard = () => {
    setFieldValue('method', config.newKey);
    setFieldValue('methodDetail', null);
    setActive(config.newKey);
  };

  const closeModal = () => {
    handleClose();
    setActive(config.newKey);
  };

  const handleBack = () => (isNew && canGoBack ? goToSavedMethods() : closeModal());

  const isAch = kind === PAYMENT_ENTRY_KINDS.ACH;

  return (
    <FormLoadErrorContext.Provider value={setFormLoadError}>
      <Modal
        isOpen={isOpen}
        handleClose={closeModal}
        isCentered
        modalCenterAnimation
        bodyClassName="p-0"
        customTitle={
          <PaymentEntryHeader
            config={config}
            isNew={isNew}
            canGoBack={canGoBack}
            goBack={goToSavedMethods}
          />
        }
        footer={
          <PaymentEntryFooter
            kind={kind}
            config={config}
            isNew={isNew}
            canGoBack={canGoBack}
            processor={processor}
            onClose={handleBack}
            onRetryFormLoad={formLoadError?.retry}
          />
        }
        overlayClassName="xs:!p-0"
        className={cn('xs:!rounded-none', PAYMENT_MODAL_SIZE)}>
        <div className="flex h-full min-h-full flex-col">
          <div className="flex w-full flex-col items-center gap-4 px-6 py-4 xs:px-4">
            <InformationCard
              icon={formLoadError ? FORM_LOAD_ERROR_INFO.icon : config.icon}
              iconSize={120}
              title={formLoadError ? FORM_LOAD_ERROR_INFO.heading : config.heading}
              description={
                formLoadError
                  ? formLoadError.message || FORM_LOAD_ERROR_INFO.description
                  : config.description
              }
              reserveDescriptionLines
            />
            <div key={isNew ? 'new' : 'saved'} className="w-full animate-enter-up">
              {isResolvingMethods ? (
                <div
                  role="status"
                  aria-live="polite"
                  aria-label="Loading saved payment methods"
                  className="flex w-full justify-center py-10">
                  <Loader color="primary" size={32} />
                </div>
              ) : hasMethodsError ? (
                <div className="flex w-full flex-col items-center gap-3 py-10">
                  <Typography variant="p" size="small" color="neutral-700" className="text-center">
                    We couldn&apos;t load your saved payment methods.
                  </Typography>
                  <Button
                    text="Try again"
                    size="small"
                    color="neutral"
                    style="outlined"
                    icon="refresh-linear"
                    onClick={() => refetch()}
                  />
                </div>
              ) : isAch ? (
                <PayWithAchContent
                  savedACHs={methods}
                  active={activeKey}
                  setActive={canGoBack ? setActive : () => {}}
                  scrollRef={scrollRef}
                  isFetchingNextPage={isFetchingNextPage}
                  values={values}
                  NewAchComponent={PairedNewAch}
                  hideButton
                />
              ) : (
                <CardEntryContent
                  cards={methods}
                  processor={processor}
                  patientId={patientId}
                  selectedCardId={values.methodDetail}
                  isNew={isNew}
                  newMethodKey={config.newKey}
                  onSelectCard={selectCard}
                  onUseAnotherCard={useAnotherCard}
                  scrollRef={scrollRef}
                  isFetchingNextPage={isFetchingNextPage}
                />
              )}
            </div>
          </div>
        </div>
      </Modal>
    </FormLoadErrorContext.Provider>
  );
};

export default PaymentEntryModal;
